import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import Home from './pages/Home';
import Sorteos from './pages/Sorteos';
import Cofres from './pages/Cofres';
import MiRed from './pages/MiRed';
import Admin from './pages/Admin';
import SecureAdminLogin from './pages/SecureAdminLogin';
import Header from './components/Header';
import AuthGate from './components/AuthGate';
import ChatBotWidget from './components/ChatBotWidget';
import { getActiveRaffles, getCart, getMe } from './lib/api';
import './App.css';

// Hide chatbot on admin pages
const ChatBotRouteWrapper = ({ isLoggedIn }) => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/secure-admin')) return null;
  return <ChatBotWidget isLoggedIn={isLoggedIn} />;
};

// Protected Admin Route - redirects to secure login if no admin token
const ProtectedAdminRoute = ({ children }) => {
  const adminToken = localStorage.getItem('admin_token');
  if (!adminToken) {
    return <Navigate to="/secure-admin-login" replace />;
  }
  return children;
};

export const AppContext = createContext();
export const useApp = () => useContext(AppContext);

function App() {
  const [raffles, setRaffles] = useState([]);
  const [selectedRaffleId, setSelectedRaffleId] = useState(null);
  const [cart, setCart] = useState({ chests: [], total: 0, count: 0, mode: 'basico' });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const selectedRaffle = raffles.find(r => r.raffle_id === selectedRaffleId) || raffles[0] || null;

  const fetchRaffles = useCallback(async () => {
    try {
      const res = await getActiveRaffles();
      setRaffles(res.data.raffles || []);
      if (!selectedRaffleId && res.data.raffles?.length > 0) {
        setSelectedRaffleId(res.data.raffles[0].raffle_id);
      }
    } catch (err) { console.error('Error fetching raffles:', err); }
  }, [selectedRaffleId]);

  const fetchCart = useCallback(async () => {
    if (!user || !selectedRaffleId) return;
    try {
      const res = await getCart(selectedRaffleId);
      setCart(res.data);
    } catch (err) { console.error('Error fetching cart:', err); }
  }, [user, selectedRaffleId]);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('cofres_user_token');
    if (token) {
      try {
        const res = await getMe();
        if (res.data.verified) setUser(res.data);
        else { localStorage.removeItem('cofres_user_token'); localStorage.removeItem('cofres_user'); }
      } catch { localStorage.removeItem('cofres_user_token'); localStorage.removeItem('cofres_user'); }
    }
  }, []);

  const login = (userData, token) => {
    localStorage.setItem('cofres_user_token', token);
    localStorage.setItem('cofres_user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('cofres_user_token');
    localStorage.removeItem('cofres_user');
    setUser(null);
    setCart({ chests: [], total: 0, count: 0, mode: 'basico' });
  };

  useEffect(() => {
    const init = async () => { await fetchRaffles(); await checkAuth(); setLoading(false); };
    init();
    const interval = setInterval(fetchRaffles, 30000);
    return () => clearInterval(interval);
  }, [fetchRaffles, checkAuth]);

  useEffect(() => { if (user && selectedRaffleId) fetchCart(); }, [user, selectedRaffleId, fetchCart]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090A0D] flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl font-heading font-bold text-[#FFC640] mb-4">COFRES MÁGICOS</div>
          <div className="w-8 h-8 border-2 border-[#FFC640] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{ raffles, selectedRaffle, selectedRaffleId, setSelectedRaffleId, cart, user, fetchRaffles, fetchCart, login, logout }}>
      <div 
        className="app-noise min-h-screen relative"
        style={{
          backgroundColor: '#090A0D',
          backgroundImage: 'url(/images/background-inner.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        <Router>
          <Routes>
            <Route path="/secure-admin-login" element={<SecureAdminLogin />} />
            {/* Redirect old /admin/whatsapp to main admin (messaging is now integrated) */}
            <Route path="/admin/whatsapp" element={<Navigate to="/admin" replace />} />
            <Route path="/admin/*" element={<ProtectedAdminRoute><Admin /></ProtectedAdminRoute>} />
            <Route path="/registro" element={<><Header /><AuthGate /></>} />
            <Route path="/cofres" element={<><Header /><Sorteos /></>} />
            <Route path="/sorteo/:raffleId/cofres" element={<><Header /><Cofres /></>} />
            <Route path="/mi-red" element={<><Header />{user ? <MiRed /> : <AuthGate />}</>} />
            <Route path="/*" element={<><Header /><Home /></>} />
          </Routes>
          <ChatBotRouteWrapper isLoggedIn={!!user} />
        </Router>
        <Toaster position="top-center" richColors />
      </div>
    </AppContext.Provider>
  );
}

export default App;
